import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Col, Form, Row} from 'react-bootstrap';
import axios from 'axios';
import {BaseModal} from 'components/modals';
import {Button} from '@/components/ui';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import SelectField from '@/components/forms/fields/SelectField';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {useDateChangeListener} from '@/utils/useDateDependentDataRefresh';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {outboundLogProviderLabel, outboundLogStatusLabel} from '@/views/admin/notification/outboundLogLabels';

interface OutboundLogRow {
    id: number;
    createdOn: string;
    channel: string;
    status: string;
    recipients: string[];
    subject: string;
    provider: string;
    body?: string | null;
    errors?: Record<string, unknown> | null;
    correlationId: string;
    metadata?: Record<string, unknown> | null;
    recipientUserIds?: number[] | null;
}

const bodyLooksLikeHtml = (text: string): boolean => {
    const t = text.trim();
    return t.length > 0 && t[0] === '<';
};

interface OutboundLogsResponse {
    results: OutboundLogRow[];
    pages: number[];
    count: number;
}

interface AppliedFilters {
    recipientEmail: string;
    status: string;
}

const emptyFilters: AppliedFilters = {
    recipientEmail: '',
    status: '',
};

export interface OutboundNotificationLogsViewProps {
    hideHelpText?: boolean;
}

const OutboundNotificationLogsView: React.FC<OutboundNotificationLogsViewProps> = React.memo(({hideHelpText = false}) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUser} = useOptimizedConfig();
    const canView = hasPermission('fs.notification.outbound_log.read', userData?.permissions, true);

    const [rows, setRows] = useState<OutboundLogRow[]>([]);
    const [pages, setPages] = useState<number[]>([]);
    const [count, setCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [applied, setApplied] = useState<AppliedFilters>(emptyFilters);
    const [draft, setDraft] = useState<AppliedFilters>(emptyFilters);
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const [detailRow, setDetailRow] = useState<OutboundLogRow | null>(null);

    const statusSelectOptions = useMemo(
        () => [
            {value: '', label: i18n.t('outbound_log_filter_any_status.text')},
            {value: 'sent', label: i18n.t('outbound_log_status_sent.text')},
            {value: 'failed', label: i18n.t('outbound_log_status_failed.text')},
        ],
        [],
    );

    const detailHtmlIframeSrc = useMemo(() => {
        if (!detailRow?.id || !detailRow.body || !bodyLooksLikeHtml(detailRow.body)) {
            return null;
        }
        const tpl = (window as Window & {URLS?: {outboundNotificationLogBodyHtml?: string}}).URLS?.outboundNotificationLogBodyHtml;
        if (!tpl) {
            return null;
        }
        return tpl.replace(':obj_id', String(detailRow.id));
    }, [detailRow?.id, detailRow?.body]);

    const fetchLogs = useCallback(async (): Promise<void> => {
        const url = window.URLS?.outboundNotificationLogs;
        if (!canView || !url) {
            return;
        }
        setLoading(true);
        try {
            const params: Record<string, string | number> = {page};
            if (applied.recipientEmail.trim()) {
                params.recipientEmail = applied.recipientEmail.trim();
            }
            if (applied.status) {
                params.status = applied.status;
            }
            const {data} = await axios.get<OutboundLogsResponse>(url, {params});
            setRows(data.results || []);
            setPages(data.pages || []);
            setCount(data.count ?? 0);
        } catch (e) {
            console.error(e);
            setRows([]);
            setPages([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, [canView, page, applied]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useDateChangeListener(fetchLogs);

    const applyFilters = (): void => {
        setPage(1);
        setApplied({...draft});
    };

    const resetFilters = (): void => {
        setDraft(emptyFilters);
        setApplied(emptyFilters);
        setPage(1);
    };

    const truncate = (s: string, n: number): string => {
        if (!s || s.length <= n) {
            return s || '';
        }
        return `${s.slice(0, n)}…`;
    };

    const metadataSummary = (row: OutboundLogRow): string => {
        const m = row.metadata;
        if (!m || typeof m !== 'object') {
            return '—';
        }
        const tk = m.template_key ?? m.templateKey;
        const src = m.source;
        const parts: string[] = [];
        if (typeof tk === 'string') {
            parts.push(tk);
        }
        if (typeof src === 'string') {
            parts.push(src);
        }
        return parts.length ? truncate(parts.join(' · '), 48) : '—';
    };

    if (loadingUser) {
        return (
            <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px">
                <div />
            </LoadingWrapper>
        );
    }

    if (!canView) {
        return (
            <>
                {!hideHelpText ? <HelpTextBlock helpText={i18n.t('outbound_log_help.text')} /> : null}
                <div className="alert alert-danger" role="alert">
                    {i18n.t('outbound_log_no_permission.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('outbound_log_col_created.text'),
            accessorKey: 'createdOn',
            cell: (cell: { row: { original: OutboundLogRow } }) => (
                configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false}) : ''
            ),
        },
        {
            header: i18n.t('outbound_log_col_status.text'),
            accessorKey: 'status',
            cell: (cell: { row: { original: OutboundLogRow } }) => outboundLogStatusLabel(cell.row.original.status),
        },
        {
            header: i18n.t('outbound_log_col_provider.text'),
            accessorKey: 'provider',
            cell: (cell: { row: { original: OutboundLogRow } }) => outboundLogProviderLabel(cell.row.original.provider),
        },
        {
            header: i18n.t('outbound_log_col_recipients.text'),
            cell: (cell: { row: { original: OutboundLogRow } }) => (
                <span title={cell.row.original.recipients?.join(', ')}>
                    {truncate(cell.row.original.recipients?.join(', ') || '', 40)}
                </span>
            ),
        },
        {
            header: i18n.t('outbound_log_col_subject.text'),
            cell: (cell: { row: { original: OutboundLogRow } }) => (
                <span title={cell.row.original.subject}>{truncate(cell.row.original.subject, 50)}</span>
            ),
        },
        {
            header: i18n.t('outbound_log_col_context.text'),
            cell: (cell: { row: { original: OutboundLogRow } }) => metadataSummary(cell.row.original),
        },
        {
            header: i18n.t('outbound_log_col_actions.text'),
            cell: (cell: { row: { original: OutboundLogRow } }) => (
                <div className="d-flex justify-content-end">
                    <Button
                        variant="info"
                        size="sm"
                        onClick={() => {
                            setDetailRow(cell.row.original);
                            setDetailOpen(true);
                        }}
                    >
                        {i18n.t('jt_show.text')}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <>
            {!hideHelpText ? <HelpTextBlock helpText={i18n.t('outbound_log_help.text')} /> : null}
            <Row className="align-items-center mb-2 g-2 flex-wrap flex-md-nowrap">
                <Col xs={12} md={2} className="min-w-0">
                    <Form
                        className="mb-0 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            size="sm"
                            placeholder={i18n.t('outbound_log_filter_recipient_placeholder.text')}
                            value={draft.recipientEmail}
                            onChange={(e) => setDraft({...draft, recipientEmail: e.target.value})}
                        />
                    </Form>
                </Col>
                <Col xs={12} sm="auto" className="flex-shrink-0 d-flex align-items-center flex-wrap">
                    <Form.Label className="mr-2 mb-0 text-nowrap col-form-label col-form-label-sm">
                        {i18n.t('outbound_log_col_status.text')}
                    </Form.Label>
                    <SelectField
                        name="outbound_log_status"
                        compact
                        value={
                            statusSelectOptions.find((o) => o.value === draft.status) ?? {
                                value: '',
                                label: i18n.t('outbound_log_filter_any_status.text'),
                            }
                        }
                        onChange={(selectedOption) => {
                            setDraft({...draft, status: selectedOption?.value ?? ''});
                        }}
                        options={statusSelectOptions}
                        customStyles={{
                            control: (provided: Record<string, unknown>) => ({
                                ...provided,
                                minHeight: '31px',
                                minWidth: '150px',
                                fontSize: '0.875rem',
                            }),
                        }}
                        isSearchable={false}
                    />
                </Col>
                <Col xs={12} sm="auto" className="d-flex align-items-center flex-shrink-0">
                    <Button variant="primary" size="sm" className="mb-0 mr-3 align-self-center" onClick={applyFilters}>
                        {i18n.t('outbound_log_apply_filters.text')}
                    </Button>
                    <Button variant="outline-secondary" size="sm" className="mb-0 align-self-center" onClick={resetFilters}>
                        {i18n.t('outbound_log_reset_filters.text')}
                    </Button>
                </Col>
            </Row>

            {loading ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px">
                    <div />
                </LoadingWrapper>
            ) : (
                <div className="card">
                    <div className="card-body">
                        {rows.length === 0 ? (
                            <span className="text-muted">{i18n.t('jt_data_available.text')}</span>
                        ) : (
                            <>
                                <div className="text-muted small mb-2">
                                    {i18n.t('outbound_log_total_count.text', {count})}
                                </div>
                                <TableComponent data={rows} columns={columns} />
                                <PaginationComponent
                                    pages={pages}
                                    currentPage={page}
                                    onPageChange={setPage}
                                    hideWhenSinglePage={false}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}

            <BaseModal
                isOpen={detailOpen}
                onClose={() => {
                    setDetailOpen(false);
                    setDetailRow(null);
                }}
                title={i18n.t('outbound_log_detail_title.text')}
                size="xl"
            >
                {detailRow ? (
                    <div className="small">
                        <p><strong>ID:</strong> {detailRow.id}</p>
                        <p><strong>{i18n.t('outbound_log_col_created.text')}:</strong>{' '}
                            {configLoaded ? formatDateTime(detailRow.createdOn, true, {setZone: false}) : detailRow.createdOn}
                        </p>
                        <p><strong>correlationId:</strong> {detailRow.correlationId}</p>
                        <p><strong>{i18n.t('outbound_log_col_status.text')}:</strong>{' '}
                            {outboundLogStatusLabel(detailRow.status)}
                        </p>
                        <p><strong>{i18n.t('outbound_log_col_provider.text')}:</strong>{' '}
                            {outboundLogProviderLabel(detailRow.provider)}
                        </p>
                        <p><strong>{i18n.t('outbound_log_col_subject.text')}:</strong> {detailRow.subject}</p>
                        <p><strong>{i18n.t('outbound_log_col_recipients.text')}:</strong> {detailRow.recipients?.join(', ')}</p>
                        {detailRow.recipientUserIds && detailRow.recipientUserIds.length > 0 ? (
                            <p><strong>{i18n.t('outbound_log_col_recipient_user_ids.text')}:</strong>{' '}
                                {detailRow.recipientUserIds.join(', ')}
                            </p>
                        ) : null}
                        <h6 className="mt-3">{i18n.t('outbound_log_body_heading.text')}</h6>
                        {detailRow.body ? (
                            bodyLooksLikeHtml(detailRow.body) ? (
                                <iframe
                                    key={detailHtmlIframeSrc ?? `srcdoc-${detailRow.id}`}
                                    title={i18n.t('outbound_log_body_heading.text')}
                                    className="w-100 border rounded bg-white"
                                    style={{minHeight: '320px'}}
                                    sandbox={detailHtmlIframeSrc ? 'allow-same-origin' : ''}
                                    src={detailHtmlIframeSrc ?? undefined}
                                    srcDoc={detailHtmlIframeSrc ? undefined : detailRow.body}
                                />
                            ) : (
                                <pre className="bg-light p-2 rounded small" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                                    {detailRow.body}
                                </pre>
                            )
                        ) : (
                            <p className="text-muted mb-0">—</p>
                        )}
                        <h6 className="mt-3">metadata</h6>
                        <pre className="bg-light p-2 rounded">{JSON.stringify(detailRow.metadata ?? null, null, 2)}</pre>
                        <h6 className="mt-3">errors</h6>
                        <pre className="bg-light p-2 rounded">{JSON.stringify(detailRow.errors ?? null, null, 2)}</pre>
                    </div>
                ) : null}
            </BaseModal>
        </>
    );
});

OutboundNotificationLogsView.displayName = 'OutboundNotificationLogsView';

export default OutboundNotificationLogsView;
